/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * useWebSpeech — thin, safe wrapper around window.SpeechRecognition.
 *
 * Zero cost, zero cloud. Uses the browser's native Web Speech API which on
 * Chrome/Edge routes to Google's engine anonymously (per platform TOS), on
 * Safari uses on-device dictation. Falls back to a no-op API when the browser
 * does not support it (e.g. Firefox stable).
 *
 * Contract:
 *   const { supported, listening, transcript, interim, start, stop, reset } =
 *     useWebSpeech({ lang: 'it-IT', continuous: true, interim: true, onFinal });
 *
 *   • `transcript` is the final text (accumulated across `onresult` events).
 *   • `interim` is the current in-flight guess (updates live).
 *   • `onFinal(text)` fires each time a stable sentence is committed.
 *   • `start()` requests mic permission; `stop()` releases it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const getRecognitionCtor = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export const useWebSpeech = ({
  lang = 'it-IT',
  continuous = true,
  interim = true,
  onFinal,
} = {}) => {
  const Ctor = getRecognitionCtor();
  const supported = !!Ctor;

  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState(null);

  // Guard against strict-mode double-mount recreating recognizer with stale callbacks.
  const onFinalRef = useRef(onFinal);
  useEffect(() => { onFinalRef.current = onFinal; }, [onFinal]);

  useEffect(() => {
    if (!supported) return;
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = continuous;
    rec.interimResults = interim;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let finalChunk = '';
      let interimChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) finalChunk += r[0].transcript;
        else interimChunk += r[0].transcript;
      }
      if (finalChunk) {
        setTranscript((t) => (t + ' ' + finalChunk.trim()).trim());
        if (typeof onFinalRef.current === 'function') {
          try { onFinalRef.current(finalChunk.trim()); } catch (e) { /* swallow */ }
        }
      }
      setInterimText(interimChunk);
    };

    rec.onerror = (event) => {
      setError(event?.error || 'speech-error');
      setListening(false);
    };
    rec.onend = () => setListening(false);

    recRef.current = rec;
    return () => {
      try { rec.stop(); } catch (e) { /* ignore */ }
      recRef.current = null;
    };
  }, [supported, Ctor, lang, continuous, interim]);

  const start = useCallback(() => {
    if (!recRef.current) return;
    setError(null);
    try {
      recRef.current.start();
      setListening(true);
    } catch (e) {
      // .start() throws if already listening — safe to ignore.
      setListening(true);
    }
  }, []);

  const stop = useCallback(() => {
    if (!recRef.current) return;
    try { recRef.current.stop(); } catch (e) { /* ignore */ }
    setListening(false);
  }, []);

  const reset = useCallback(() => {
    setTranscript('');
    setInterimText('');
    setError(null);
  }, []);

  return { supported, listening, transcript, interim: interimText, error, start, stop, reset };
};

export default useWebSpeech;
